import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { AnchorResolver } from '@src/editor/models/layout/AnchorResolver';
import { Scene } from '@src/editor/models/layout/Scene';
import { SceneService } from '@src/services/SceneService';
import { LogManager } from '@src/utility/Logger';

import { AbstractCommand } from '../AbstractCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { TranscriptClipboardEntry } from '@src/editor/types/transcriptClipboard';
import type { VideoEditor } from '@src/editor/VideoEditor';

export const logger = LogManager.getLogger('TranscriptPasteCommand');

export interface PasteInsertionResult {
  targetElement: VideoTranscriptElement;
  insertedElements: VideoTranscriptElement[];
  splitParts?: {
    firstPart: VideoTranscriptElement;
    secondPart: VideoTranscriptElement;
  };
}

/**
 * TranscriptPasteCommand – pastes clipboard transcript segments at a specific
 * position within a VideoTranscriptElement. The command supports:
 *   • Insert before a VideoTranscriptElement (at the start)
 *   • Insert after a VideoTranscriptElement (at the end)
 *   • Insert in the middle of a VideoTranscriptElement (requires splitting)
 *
 * When inserting in the middle of a VideoTranscriptElement, the element is
 * split at the specified word index and the clipboard segments are inserted
 * between the split parts. RemovedTranscriptClips are handled by ensuring
 * inserted segments are placed before them when appropriate.
 */
export class TranscriptPasteCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptPasteCommand';
  }

  private readonly clipboardEntries: TranscriptClipboardEntry[];
  private readonly clipSegmentId: string;
  private readonly atIndex: number; // 0 based index
  private readonly after: boolean; // if true, paste after atIndex (to correctly handle insertion after the last index), else before

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;
  private readonly sceneService = new SceneService();
  private readonly anchorResolver = new AnchorResolver();

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  private timelineSegmentAdded: ElementResizeResult | null = null;

  constructor(
    editor: VideoEditor,
    clipboardEntries: TranscriptClipboardEntry[],
    clipSegmentId: string,
    atIndex: number,
    after: boolean,
  ) {
    super(editor);

    this.clipboardEntries = clipboardEntries;
    this.clipSegmentId = clipSegmentId;
    this.atIndex = atIndex;
    this.after = after;

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack() || null;
    this.transcriptTrackElements = this.transcriptTrack
      ? this.transcriptTrack.transcriptSegmentElements
      : [];
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;

    if (!track || !this.clipboardEntries.length) {
      console.log(
        'TranscriptPasteCommand: Early return - no track or no segments',
      );
      return false;
    }

    // Find the target video element containing the insertion point
    const targetElement = this.findTargetVideoElement();

    if (!targetElement) {
      console.error('TranscriptPasteCommand: Target video element not found');
      return false;
    }
    // Determine insertion strategy based on position within the element
    const insertionResult = this.performPasteInsertion(targetElement);
    if (!insertionResult) {
      console.log('TranscriptPasteCommand: No elements were inserted');
      return false;
    }

    const { insertedElements } = insertionResult;

    // Adjust scenes for inserted elements
    this.sceneService.adjustScenesForInsertedElements(
      this.editor.timeline,
      insertionResult,
    );

    // After operation is successfull, mark segments as added
    insertedElements.forEach(element =>
      element.getClipTranscriptSegment().markElementsAsAdded(),
    );

    this.restoreScenesForInsertedElements(
      this.clipboardEntries.map((entry, idx) => ({
        insertedElement: insertedElements[idx],
        scenes: entry.scenes,
        sourceElementId: entry.sourceElementId,
      })),
    );

    this.sceneService.validateAndAdjustScenes(this.editor.timeline);

    // Recalculate timeline duration and reconcile dependent tracks
    this.recalculateTimeline();
    this.editor.markDirty('transcript_paste');

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Paste transcript segments at ${this.clipSegmentId}[${
      this.atIndex
    }]${this.after ? '+' : ''}`;
  }

  getTimelineSegmentAdded() {
    return this.timelineSegmentAdded;
  }

  private restoreScenesForInsertedElements(
    insertedElementsWithScenes: {
      insertedElement: VideoTranscriptElement;
      scenes: Scene[];
      sourceElementId: string;
    }[],
  ): void {
    if (!insertedElementsWithScenes.length) {
      return;
    }
    // Flatten all scenes from all clipboard entries
    const allScenes = insertedElementsWithScenes.flatMap(entry => entry.scenes);

    // Merge consecutive scenes with same scene ID
    const mergedScenes = allScenes.reduce<Scene[]>((acc, current) => {
      const previous = acc[acc.length - 1];

      if (previous && previous.id === current.id) {
        // Merge with previous: extend end anchor to current's end anchor
        const mergedScene = new Scene({
          id: previous.id,
          anchorStart: previous.anchorStart,
          anchorEnd: current.anchorEnd,
          layout: previous.layout.clone(),
        });

        acc[acc.length - 1] = mergedScene;
      } else {
        // Different scene, add as new
        acc.push(current);
      }

      return acc;
    }, []);

    // Map source element IDs to inserted elements
    const sourceToInsertedMap = new Map<string, VideoTranscriptElement>();
    insertedElementsWithScenes.forEach(entry => {
      sourceToInsertedMap.set(entry.sourceElementId, entry.insertedElement);
    });

    // Insert merged scenes, remapping anchors to inserted elements
    mergedScenes.forEach(scene => {
      if (
        typeof scene.anchorStart === 'string' ||
        typeof scene.anchorEnd === 'string'
      ) {
        logger.error('Scene from clipboard is not anchored to any element');
        return;
      }
      const anchorStartTarget = sourceToInsertedMap.get(
        scene.anchorStart.elementId,
      )?.id;
      const anchorEndTarget = sourceToInsertedMap.get(scene.anchorEnd.elementId)
        ?.id;

      if (!anchorStartTarget || !anchorEndTarget) {
        logger.warn('No target element for scene restoration', {
          anchorStartTarget,
          anchorEndTarget,
        });
        return;
      }

      const newStartAnchor = this.sceneService.retargetAnchor(
        scene.anchorStart,
        anchorStartTarget,
      );

      const newEndAnchor = this.sceneService.retargetAnchor(
        scene.anchorEnd,
        anchorEndTarget,
      );

      this.sceneService.addScene(
        this.editor.timeline,
        new Scene({
          anchorStart: newStartAnchor,
          anchorEnd: newEndAnchor,
          layout: scene.layout,
        }),
      );
    });
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  /**
   * Finds the target VideoTranscriptElement containing the insertion point
   * Only insertion relative to VideoTranscriptElements is supported
   */
  private findTargetVideoElement(): VideoTranscriptElement | null {
    const targetElement = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.clipSegmentId,
    );

    // Only allow insertion relative to VideoTranscriptElements
    if (targetElement instanceof VideoTranscriptElement) {
      return targetElement;
    }

    return null;
  }

  /**
   * Performs the paste insertion using the appropriate strategy
   * Converts command inputs to generic method arguments
   */
  private performPasteInsertion(
    targetElement: VideoTranscriptElement,
  ): PasteInsertionResult | null {
    const segmentsToInsert = this.clipboardEntries.map(entry => entry.segment);

    const segment = targetElement.getClipTranscriptSegment();
    const wordsWithPunctuation = segment.getWordsWithPunctuation();

    // Determine effective insertion index
    const effectiveIndex = this.after ? this.atIndex + 1 : this.atIndex;

    // Check if we're inserting at boundaries or in the middle
    const isAtStart =
      effectiveIndex <= wordsWithPunctuation.indexOf(segment.firstWord!);
    const isAtEnd =
      effectiveIndex > wordsWithPunctuation.indexOf(segment.lastWord!);

    if (isAtStart) {
      // Insert before the video element
      const insertionTime = targetElement.time;
      const inserted =
        this.transcriptTrackManager.insertTranscriptSegmentsAtTime(
          segmentsToInsert,
          insertionTime,
          targetElement,
          TimelineMode.RIPPLE,
        );

      return inserted
        ? {
            targetElement,
            insertedElements: inserted,
          }
        : null;
    } else if (isAtEnd) {
      // Insert after the video element
      const insertionTime = targetElement.end_time;
      const inserted =
        this.transcriptTrackManager.insertTranscriptSegmentsAtTime(
          segmentsToInsert,
          insertionTime,
          targetElement,
          TimelineMode.RIPPLE,
        );

      return inserted
        ? {
            targetElement,
            insertedElements: inserted,
          }
        : null;
    } else {
      // Insert in the middle - need to split the video element
      const wordsAfterInsertionPosition = segment
        .getWordsWithPunctuation()
        .filter((_, idx) => idx >= effectiveIndex)
        .filter(
          (token): token is ClipTranscriptTextToken => token.type === 'text',
        );

      if (!wordsAfterInsertionPosition.length) {
        console.error(
          'TranscriptPasteCommand: No words found after insertion position for identifying splitting time',
        );
        return null;
      }

      const splitResult =
        this.transcriptTrackManager.insertTranscriptSegmentsBySplitting(
          targetElement,
          wordsAfterInsertionPosition[0].media_ts,
          segmentsToInsert,
          TimelineMode.RIPPLE,
        );

      return splitResult
        ? {
            targetElement,
            insertedElements: splitResult.insertedElements,
            splitParts: {
              firstPart: splitResult.firstPart,
              secondPart: splitResult.secondPart,
            },
          }
        : null;
    }
  }
}
